import { useMutation, useQueryClient } from '@tanstack/react-query';
import SharedDialog from '../../../../shared/shared-dialog';
import { useState } from 'react';
import { Calendar } from '../../../../../types';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { timezones } from '../../../../utils/timezones';
import { UserKeys, createCalendar } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

interface CalendarAddDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  isAllUsers: boolean;
}

export default function CalendarAddDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  isAllUsers,
}: CalendarAddDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isCreating } = useMutation({
    mutationFn: (data: Partial<Calendar>) =>
      createCalendar(userEmail, data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar created successfully!',
      });

      queryClient.setQueriesData(
        { queryKey: [UserKeys.Calendars, userEmail] },
        (oldData: Calendar[] | undefined) => {
          if (oldData) {
            return [...oldData, data];
          } else {
            return [data];
          }
        }
      );
      setChangeLogMessage('');
      reset();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating a calendar. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.create_calendar
          : analytics.user_management.user_details.calendars.create_calendar,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isLoading, errors },
  } = useForm({
    defaultValues: {
      summary: '',
      description: '',
      location: '',
      timeZone: '',
    },
  });

  const onSubmit: SubmitHandler<Partial<Calendar>> = (data) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.CreateCalendars.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
      }}
      title="Add Calendar"
      dialogContent={
        <Box>
          <Grid
            container
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid size={{ xs: 6 }} gap={4}>
              <Controller
                name="summary"
                control={control}
                rules={{
                  required: 'Calendar Name is required.',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoFocus
                    label="Calendar Name"
                    autoComplete="off"
                    error={!!errors.summary}
                    helperText={errors?.summary?.message || ' '}
                    sx={{ minWidth: 250 }}
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    autoComplete="off"
                    helperText={errors?.description?.message || ' '}
                    sx={{ minWidth: 250 }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Location"
                    autoComplete="off"
                    helperText={errors?.location?.message || ' '}
                    sx={{ minWidth: 250 }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="timeZone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'timeZone',
                    }}
                    InputLabelProps={{
                      htmlFor: 'timeZone',
                    }}
                    select
                    label="Time Zone"
                    sx={{ minWidth: 250 }}
                  >
                    {timezones.map((timezone) => (
                      <MenuItem key={timezone} value={timezone}>
                        {timezone}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() =>
              mutate({
                summary: getValues('summary'),
                description: getValues('description'),
                location: getValues('location'),
                timeZone: getValues('timeZone'),
              })
            }
            mutateLoading={isCreating}
          />
        </Box>
      }
      primaryButton={{
        title: 'Add Calendar',
        action: handleSubmit(onSubmit),
        loading: isCreating || isLoading,
        disabled: isCreating || isLoading,
      }}
    />
  );
}
